import { useState } from 'react';
import { Paper, Stack, Typography, Chip, TextField, Button, Box, Tooltip } from '@mui/material';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import { useToast } from '../../contexts/ToastContext';
import type { Catalogue, CatalogueMatchSignal } from '@traaker/shared';

export const SIGNALS: { id: CatalogueMatchSignal; label: string; help: string }[] = [
  { id: 'domain', label: 'Domaine cité', help: "Un lien vers votre domaine est cité — prouve que LE RETAILER est recommandé (marketplace, produits génériques : Auchan)." },
  { id: 'name', label: 'Nom du produit', help: "Le nom du produit apparaît dans la réponse (marque fabricante : Coca-Cola)." },
  { id: 'ref', label: 'Référence / SKU', help: "La référence produit apparaît (références propres distinctives : Decathlon)." },
  { id: 'brand', label: 'Marque', help: "Un terme de marque configuré apparaît dans la réponse." },
];

/**
 * Colors linking a signal chip to the column it reads in the items example
 * table just below (CatalogueDetail). 'brand' has no matching column — it's
 * matched against AI-answer text, not a per-item field — so it's omitted and
 * keeps the default chip look.
 */
export const SIGNAL_ACCENT_COLOR: Partial<Record<CatalogueMatchSignal, string>> = {
  name: '#00A88A',
  ref: '#2563EB',
  domain: '#2E7D32',
};

/**
 * Per-catalogue measurement match config. Which signals prove the retailer / its
 * products were recommended differs by retailer nature — a marketplace relies on
 * the cited DOMAIN, a manufacturer brand on the product NAME, etc.
 */
export function MatchConfigCard({
  brandId,
  catalogue,
  onChange,
  onSignalsChange,
}: {
  brandId: string;
  catalogue: Catalogue;
  onChange: (c: Catalogue) => void;
  /** Fires live as signals are toggled (before save) so the caller can highlight the matching columns in the items preview below. */
  onSignalsChange?: (signals: CatalogueMatchSignal[]) => void;
}) {
  const { show } = useToast();
  const [signals, setSignals] = useState<CatalogueMatchSignal[]>(
    catalogue.matchSignals?.length ? catalogue.matchSignals : ['domain', 'name'],
  );
  const [brandTerms, setBrandTerms] = useState((catalogue.matchBrandTerms || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const toggle = (s: CatalogueMatchSignal) => {
    setSignals((prev) => {
      const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s];
      onSignalsChange?.(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await retailerCatalogApi.updateMatchConfig(brandId, catalogue.catalogueId, {
        signals,
        brandTerms: brandTerms.split(',').map((t) => t.trim()).filter(Boolean),
      });
      onChange(updated);
      setDirty(false);
      show('Configuration enregistrée', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Enregistrement impossible', { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Détection des recommandations (Visibilité GEO)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Comment décider qu'une réponse IA recommande ce retailer. Choisissez selon sa nature :
        marketplace → domaine cité ; marque fabricante → nom ; références propres → SKU.
        Le tableau ci-dessous surligne la colonne concernée.
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {SIGNALS.map((s) => {
          const active = signals.includes(s.id);
          const accent = SIGNAL_ACCENT_COLOR[s.id];
          return (
            <Tooltip key={s.id} title={s.help}>
              <Chip
                label={s.label}
                size="small"
                onClick={() => toggle(s.id)}
                sx={
                  active && accent
                    ? {
                        bgcolor: accent,
                        color: '#fff',
                        fontWeight: 700,
                        '&:hover': { bgcolor: accent },
                      }
                    : undefined
                }
                color={active && !accent ? 'primary' : undefined}
                variant={active ? 'filled' : 'outlined'}
              />
            </Tooltip>
          );
        })}
      </Stack>
      {signals.includes('brand') && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Les termes de marque sont appariés dans la réponse IA, pas dans le catalogue — aucune colonne n'est surlignée ci-dessous.
          </Typography>
          <TextField
            label="Termes de marque (séparés par des virgules)"
            value={brandTerms}
            onChange={(e) => {
              setBrandTerms(e.target.value);
              setDirty(true);
            }}
            size="small"
            fullWidth
            placeholder="decathlon, quechua, kalenji"
          />
        </Box>
      )}
      {dirty && (
        <Box sx={{ mt: 1.5 }}>
          <Button variant="contained" size="small" onClick={save} disabled={saving}>
            Enregistrer
          </Button>
        </Box>
      )}
    </Paper>
  );
}
